import type { OAuthClientInfo, OAuthScopes } from '@repo/utils/types/Auth';
import type { UserProfileFull, UserRelation } from '@repo/utils/types/Core';

import { Trans, useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import styled, { ThemeProvider, useTheme } from 'styled-components';

import { PrimaryButton, SecondaryButton } from '@repo/ui/components/buttons';
import Icon from '@repo/ui/components/ui/Icon';
import Image from '@repo/ui/components/ui/Image';
import Panel from '@repo/ui/components/layout/Panel';
import OrganizationAndBusinessSelect from '../../inputs/OrganizationAndBusinessSelect';

export type OAuthConsentProps = {
  /** The OAuth client ID requesting user consent */
  client: OAuthClientInfo;

  /** Callback when the user has given consent */
  onAccepted: (context: OrgBizOption) => void;

  /** Callback when the user has withheld consent */
  onDeclined: () => void;

  /** A list of requested scopes */
  requestingScopes?: OAuthScopes[];

  /** The current user */
  user: UserProfileFull;
};

export type OrgBizOption = {
  id: string;
  slug: string;
  name: string;
  type: 'organization' | 'business' | 'user' | string;
  logo?: string;
  image?: string;
};

const StyledDialog = styled(Panel)`
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 auto;
  max-width: 500px;
  padding: 20px;

  header {
    text-align: center;
  }

  header :is(h2, a) {
    font-size: 24px;
  }

  header small {
    display: block;
  }

  p,
  ul {
    margin: 0;
  }

  li {
    margin-top: 10px;
  }

  footer {
    display: flex;
    justify-content: center;
    gap: 20px;
  }
`;

const LogoBuddies = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
`;

const WarningPanel = styled.div`
  background-color: ${(props) => props.theme.brandColors.lighterBackground};
  padding: 10px;
  border-radius: 10px;
`;

export default function OAuthConsent({
  client,
  onAccepted,
  onDeclined,
  requestingScopes,
  user,
}: OAuthConsentProps) {
  const { t } = useTranslation(['commonPlat']);
  const theme = useTheme();

  const [context, setContext] = useState<OrgBizOption | null>(null);
  const [working, setWorking] = useState(false);

  /**
   * The user has given consent for the OAuth connection
   */
  const handleAccept = useCallback(() => {
    if (context) {
      setWorking(true);
      onAccepted?.(context);
    }
  }, [context, onAccepted]);

  /**
   * The user has withheld consent for the connection
   */
  const handleDecline = () => {
    setWorking(true);
    onDeclined?.();
  };

  // Lazy shorthand enabler
  const clientName = client?.name;

  const isUserContext = !context || context.type === 'user';

  // List of possible requestable scopes and their translations
  const translatedScopes: { [key: string]: string } = {
    'profile:read': !isUserContext
      ? t('commonPlat:labels.oAuthConsent.scopesDelegated.profile')
      : t('commonPlat:labels.oAuthConsent.scopes.profile'),
    'stories:read': !isUserContext
      ? t('commonPlat:labels.oAuthConsent.scopesDelegated.stories_read', {
          name: context?.name,
        })
      : t('commonPlat:labels.oAuthConsent.scopes.stories_read'),
    'stories:write': !isUserContext
      ? t('commonPlat:labels.oAuthConsent.scopesDelegated.stories_write', {
          name: context?.name,
        })
      : t('commonPlat:labels.oAuthConsent.scopes.stories_write'),
  };

  console.dir(user.relations);

  const sources: OrgBizOption[] = (user.relations || [])
    .filter((rel) => !!rel?.target)
    .filter((rel) => ['Limited Admin', 'Administrator'].includes(rel.relation))
    .map(
      (r) =>
        ({
          id: r.target.slug,
          slug: r.target.slug,
          name: r.target.name,
          type: r.target.type,
          logo: r.target.profile_photo,
          image: r.target.profile_photo,
        }) satisfies OrgBizOption,
    );

  sources.unshift({
    id: 'self',
    slug: 'self',
    name: `${t('commonPlat:dynamic.yourself')} (${user.firstName} ${user.lastName})`,
    type: 'user',
    logo: user.profilePhoto,
    image: user.profilePhoto,
  });

  useEffect(() => {
    if (sources.length === 1 && sources[0]) setContext(sources[0]);
  }, [sources]);

  return (
    <ThemeProvider theme={theme}>
      <StyledDialog>
        <header>
          <LogoBuddies>
            <Image
              square
              size="lg"
              src={client.logo_url}
              alt={t('commonPlat:labels.logoOfName', { clientName })}
            />
            <Icon name="heart" size="sm" color="red" />
            <Image
              square
              size="lg"
              src="https://files.dosomegood.ca/images/platform/brand/dosomegood.svg"
            />
          </LogoBuddies>
          <h2>
            {clientName}
            <small>{t('commonPlat:labels.oAuthConsent.title')}</small>
          </h2>
        </header>
        <fieldset>
          <label htmlFor="oauth-context-select">
            {t('commonPlat:labels.oAuthConsent.selectContext')}
          </label>
          <OrganizationAndBusinessSelect
            id="oauth-context-select"
            placeholder={t('commonPlat:labels.oAuthConsent.selectContext')}
            options={sources}
            onChange={(val: OrgBizOption) => setContext(val)}
            value={context}
            disabled={sources.length === 1}
            required
          />
          {/* TODO: Translate */}
        </fieldset>
        <article>
          <strong>{t('commonPlat:labels.oAuthConsent.access', { clientName })}</strong>
          <ul>
            {requestingScopes?.map((scope, idx) => (
              <li key={idx}>{translatedScopes[scope] || scope}</li>
            ))}
          </ul>
        </article>

        <WarningPanel>
          <p>
            <strong>{t('commonPlat:labels.oAuthConsent.warningBefore')}</strong>
          </p>
          <ul>
            <li>{t('commonPlat:labels.oAuthConsent.warningTrust', { clientName })}</li>
            <li>
              <Trans t={t} i18nKey={'commonPlat:labels.oAuthConsent.warningPrivacy'}>
                {{ clientName }} will handle your data according to their own
                <a href={client.privacyUrl} target="_blank" rel="noreferrer">
                  privacy policy
                </a>{' '}
                and
                <a href={client.termsUrl} target="_blank" rel="noreferrer">
                  terms of service
                </a>
              </Trans>
            </li>
            <li>{t('commonPlat:labels.oAuthConsent.warningUsage', { clientName })}</li>
            <li>{t('commonPlat:labels.oAuthConsent.warningRevoke')}</li>
          </ul>
        </WarningPanel>

        <footer>
          {sources.length > 0 && (
            <PrimaryButton disabled={working || !context} onClick={handleAccept}>
              {t('commonPlat:actions.accept')}
            </PrimaryButton>
          )}
          <SecondaryButton disabled={working} onClick={handleDecline}>
            {t('commonPlat:actions.decline')}
          </SecondaryButton>
        </footer>
      </StyledDialog>
    </ThemeProvider>
  );
}
